import * as XLSX from 'xlsx';

export type ImportPessoa = {
  nome: string;
  cargo: string;
  vagaAberta: boolean;
};

export type ImportDepartamento = {
  nome: string;
  pessoas: ImportPessoa[];
};

export type ImportGrupo = {
  titulo: string;
  coordenador: string | null;
  departamentos: ImportDepartamento[];
};

export type ImportDuplicata = {
  nome: string;
  ocorrencias: string[];
};

export type ImportParseResult = {
  grupos: ImportGrupo[];
  unrecognizedLines: string[];
  duplicatas: ImportDuplicata[];
  totalPessoas: number;
};

type RawRow = { seq: string | null; setor: string; cargo: string; nome: string };
type RawBlock = { titulo: string; rows: RawRow[] };

const isBlank = (value: unknown) => value === null || value === undefined || String(value).trim() === '';

const normalize = (value: string) => value.trim().replace(/\s+/g, ' ');

const normalizeKey = (value: string) => normalize(value).toLowerCase();

/** Extrai "coordenador" de títulos como "EQUIPE X - COORDENADOR DANILO". */
function splitTitulo(raw: string): { titulo: string; coordenador: string | null } {
  const texto = normalize(raw);
  const match = texto.match(/^(.*?)[-–]\s*coordenador(?:a)?\s*:?\s*(.+)$/i);
  if (match) {
    return { titulo: normalize(match[1]).replace(/[-–]\s*$/, '').trim(), coordenador: normalize(match[2]) };
  }
  return { titulo: texto, coordenador: null };
}

/**
 * Detecta blocos em planilhas sem cabeçalho, no formato observado na planilha
 * real da Ana: uma linha-título isolada ("EQUIPE X - COORDENADOR Y") seguida de
 * linhas numeradas [seq, setor, cargo, nome], até a próxima linha em branco.
 */
function detectHeuristicBlocks(rows: unknown[][]): { blocks: RawBlock[]; unrecognizedLines: string[] } {
  const blocks: RawBlock[] = [];
  const unrecognizedLines: string[] = [];
  let current: RawBlock | null = null;

  const cells = (row: unknown[]) => row.map((c) => (isBlank(c) ? '' : String(c).trim())).filter((c) => c !== '');

  for (const row of rows) {
    const filled = cells(row);
    if (filled.length === 0) {
      current = null;
      continue;
    }

    const firstCell = filled[0];
    const seqLooksNumeric = /^\d+$/.test(firstCell);

    if (seqLooksNumeric && filled.length >= 3) {
      const [, setor, cargo, nome] = filled;
      if (current && setor && cargo && nome) {
        current.rows.push({ seq: firstCell, setor, cargo, nome });
        continue;
      }
    }

    if (filled.length === 1) {
      current = { titulo: filled[0], rows: [] };
      blocks.push(current);
      continue;
    }

    current = null;
    unrecognizedLines.push(filled.join(' | '));
  }

  const nonEmptyBlocks = blocks.filter((b) => b.rows.length > 0);
  blocks
    .filter((b) => b.rows.length === 0)
    .forEach((b) => unrecognizedLines.push(`Título sem itens abaixo: "${b.titulo}"`));

  return { blocks: nonEmptyBlocks, unrecognizedLines };
}

function buildFromBlocks(blocks: RawBlock[]): { grupos: ImportGrupo[]; duplicatas: ImportDuplicata[]; totalPessoas: number } {
  const nomeOcorrencias = new Map<string, string[]>();
  let totalPessoas = 0;

  const grupos: ImportGrupo[] = blocks.map((block) => {
    const { titulo, coordenador } = splitTitulo(block.titulo);
    const departamentosPorChave = new Map<string, ImportDepartamento>();

    for (const row of block.rows) {
      const setorKey = normalizeKey(row.setor);
      let departamento = departamentosPorChave.get(setorKey);
      if (!departamento) {
        departamento = { nome: normalize(row.setor), pessoas: [] };
        departamentosPorChave.set(setorKey, departamento);
      }

      const nomeNormalizado = normalizeKey(row.nome);
      const vagaAberta = nomeNormalizado.startsWith('vaga') || nomeNormalizado === '';
      const nome = vagaAberta ? 'Vaga aberta' : normalize(row.nome);

      departamento.pessoas.push({ nome, cargo: normalize(row.cargo), vagaAberta });
      totalPessoas += 1;

      if (!vagaAberta) {
        const local = `${titulo} > ${departamento.nome}`;
        const existentes = nomeOcorrencias.get(nomeNormalizado) || [];
        existentes.push(local);
        nomeOcorrencias.set(nomeNormalizado, existentes);
      }
    }

    return { titulo, coordenador, departamentos: Array.from(departamentosPorChave.values()) };
  });

  const duplicatas: ImportDuplicata[] = Array.from(nomeOcorrencias.entries())
    .filter(([, ocorrencias]) => ocorrencias.length > 1)
    .map(([nomeKey, ocorrencias]) => ({
      nome: ocorrencias[0] ? normalize(nomeKey) : nomeKey,
      ocorrencias,
    }));

  return { grupos, duplicatas, totalPessoas };
}

function parseSpreadsheetRows(rows: unknown[][]): ImportParseResult {
  const { blocks, unrecognizedLines } = detectHeuristicBlocks(rows);
  const { grupos, duplicatas, totalPessoas } = buildFromBlocks(blocks);
  return { grupos, unrecognizedLines, duplicatas, totalPessoas };
}

/**
 * Schema XML próprio do Kora (não há amostra real da Ana em XML ainda):
 * <organograma>
 *   <grupo titulo="..." coordenador="...">
 *     <item setor="..." cargo="..." nome="..." />
 *   </grupo>
 * </organograma>
 */
function parseXmlContent(text: string): ImportParseResult {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error('XML inválido: não foi possível interpretar o arquivo.');
  }

  const unrecognizedLines: string[] = [];
  const blocks: RawBlock[] = [];

  const grupoNodes = Array.from(doc.querySelectorAll('grupo'));
  for (const grupoNode of grupoNodes) {
    const tituloAttr = grupoNode.getAttribute('titulo') || '';
    const coordenadorAttr = grupoNode.getAttribute('coordenador');
    const titulo = coordenadorAttr ? `${tituloAttr} - COORDENADOR ${coordenadorAttr}` : tituloAttr;
    if (isBlank(titulo)) {
      unrecognizedLines.push('<grupo> sem atributo "titulo"');
      continue;
    }

    const block: RawBlock = { titulo, rows: [] };
    const itemNodes = Array.from(grupoNode.querySelectorAll('item'));
    itemNodes.forEach((itemNode, index) => {
      const setor = itemNode.getAttribute('setor') || '';
      const cargo = itemNode.getAttribute('cargo') || '';
      const nome = itemNode.getAttribute('nome') || '';
      if (isBlank(setor) || isBlank(nome)) {
        unrecognizedLines.push(`<item> incompleto em "${tituloAttr}" (posição ${index + 1})`);
        return;
      }
      block.rows.push({ seq: String(index + 1), setor, cargo, nome });
    });

    if (block.rows.length > 0) blocks.push(block);
  }

  if (grupoNodes.length === 0) {
    unrecognizedLines.push('Nenhum elemento <grupo> encontrado no XML.');
  }

  const { grupos, duplicatas, totalPessoas } = buildFromBlocks(blocks);
  return { grupos, unrecognizedLines, duplicatas, totalPessoas };
}

export async function parseOrganogramaFile(file: File): Promise<ImportParseResult> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'xml') {
    const text = await file.text();
    return parseXmlContent(text);
  }

  if (extension === 'csv' || extension === 'xlsx' || extension === 'xls') {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const allRows: unknown[][] = [];
    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: true, defval: '' });
      allRows.push(...rows, []);
    });
    return parseSpreadsheetRows(allRows);
  }

  throw new Error('Formato não suportado. Envie um arquivo .csv, .xlsx, .xls ou .xml.');
}
